<template>
  <div class="driver-fees-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Driver Fees Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ recordCount }} Students · {{ feeRecordCount }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button
          class="btn-modern btn-export"
          type="button"
          :disabled="exporting || loading || !driverBlocks.length"
          @click="exportExcel"
        >
          <b-spinner
            v-if="exporting"
            small
            variant="light"
            type="grow"
            class="mr-50"
          />
          <feather-icon v-else icon="DownloadIcon" size="18" class="mr-50" />
          <span>{{ exporting ? "Exporting..." : "Export" }}</span>
        </button>
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading driver fees report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Driver Fees Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <p v-if="filterSummary" class="filter-line mb-0 mt-1">
              {{ filterSummary }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="notice" class="notice-banner">
        {{ notice }}
      </div>

      <div v-if="!driverBlocks.length" class="empty-state">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No driver fee records found for the selected criteria</p>
      </div>

      <section
        v-for="(driver, driverIndex) in driverBlocks"
        :key="'driver-' + (driver.driverID || driverIndex)"
        class="driver-block"
      >
        <div class="driver-heading">
          <div class="driver-heading__left">
            <span class="driver-heading__index">{{ driverIndex + 1 }}</span>
            <div class="driver-heading__text">
              <span class="driver-heading__name">
                {{ driver.name || "Unnamed driver" }}
              </span>
              <div class="driver-heading__meta">
                <span v-if="driver.contact">Contact: {{ driver.contact }}</span>
                <span v-if="driver.cnic"> · CNIC: {{ driver.cnic }}</span>
                <span v-if="driver.licenseNo">
                  · License: {{ driver.licenseNo }}</span
                >
                <span v-if="vehicleSummary(driver)">
                  · Vehicles: {{ vehicleSummary(driver) }}</span
                >
              </div>
            </div>
          </div>
          <span class="driver-heading__count">
            {{ formatCount(driver.studentCount) }} students
          </span>
        </div>

        <div v-if="(driver.feeTypes || []).length" class="sub-section">
          <h5 class="sub-heading">Fee Type Summary</h5>
          <div class="table-container">
            <table class="report-table fee-type-table">
              <thead>
                <tr>
                  <th class="text-left">Fee Type</th>
                  <th class="text-right">Billed</th>
                  <th class="text-right">Collected (period)</th>
                  <th class="text-right">Outstanding</th>
                  <th class="text-right">Unbilled Custom</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="type in driver.feeTypes"
                  :key="'type-' + type.feeTypeID"
                >
                  <td class="text-left font-weight-bold">
                    {{ type.feeType || "—" }}
                  </td>
                  <td class="text-right">{{ formatAmount(type.billed) }}</td>
                  <td class="text-right">{{ formatAmount(type.collected) }}</td>
                  <td class="text-right">{{ formatAmount(type.outstanding) }}</td>
                  <td class="text-right">
                    {{ formatAmount(type.unbilledCustom) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="sub-section">
          <h5 class="sub-heading">Students</h5>
          <div class="table-container">
            <table class="report-table students-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 3%">#</th>
                  <th class="text-center" style="width: 7%">GR No</th>
                  <th class="text-left" style="width: 18%">Student Name</th>
                  <th class="text-left" style="width: 14%">Father Name</th>
                  <th class="text-center" style="width: 9%">Class / Sec</th>
                  <th class="text-center" style="width: 8%">Vehicle</th>
                  <th class="text-right" style="width: 8%">Custom Fee</th>
                  <th class="text-right" style="width: 10%">Billed</th>
                  <th class="text-right" style="width: 10%">Collected</th>
                  <th class="text-right" style="width: 12%">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(student, studentIndex) in driver.students || []"
                  :key="'student-' + (student.sID || studentIndex)"
                >
                  <td class="text-center font-weight-bold">
                    {{ studentIndex + 1 }}
                  </td>
                  <td class="text-center gr-cell">{{ text(student.grNo) }}</td>
                  <td class="text-left font-weight-bold">
                    {{ text(student.name) }}
                  </td>
                  <td class="text-left">{{ text(student.father_name) }}</td>
                  <td class="text-center">{{ classSection(student) }}</td>
                  <td class="text-center">{{ studentVehicle(student) }}</td>
                  <td class="text-right">{{ formatAmount(student.customFee) }}</td>
                  <td class="text-right">{{ formatAmount(student.totalBilled) }}</td>
                  <td class="text-right">
                    {{ formatAmount(student.totalCollected) }}
                  </td>
                  <td class="text-right outstanding-cell">
                    {{ formatAmount(student.totalOutstanding) }}
                  </td>
                </tr>
                <tr v-if="!(driver.students || []).length">
                  <td colspan="10" class="text-center py-3 text-muted">
                    No students found
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="(driver.students || []).length">
                <tr class="subtotal-row">
                  <td colspan="6" class="text-right font-weight-bold">
                    Driver total
                  </td>
                  <td class="text-right font-weight-bold">—</td>
                  <td class="text-right font-weight-bold">
                    {{ formatAmount(driver.totalBilled) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatAmount(driver.totalCollected) }}
                  </td>
                  <td class="text-right font-weight-bold outstanding-cell">
                    {{ formatAmount(driver.totalOutstanding) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="sub-section">
          <h5 class="sub-heading">Fee Records</h5>
          <div class="table-container">
            <table class="report-table fee-records-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 3%">#</th>
                  <th class="text-center" style="width: 6%">GR No</th>
                  <th class="text-left" style="width: 18%">Student</th>
                  <th class="text-left" style="width: 11%">Fee Type</th>
                  <th class="text-center" style="width: 8%">Period</th>
                  <th class="text-right" style="width: 8%">Net Fee</th>
                  <th class="text-right" style="width: 8%">Received</th>
                  <th class="text-right" style="width: 9%">
                    Collected (period)
                  </th>
                  <th class="text-right" style="width: 8%">Balance</th>
                  <th class="text-center" style="width: 11%">Status</th>
                  <th class="text-center" style="width: 10%">Received On</th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(student, studentIndex) in driver.students || []"
                >
                  <tr
                    v-for="(fee, feeIndex) in student.feeRecords || []"
                    :key="
                      'fee-' +
                      (student.sID || studentIndex) +
                      '-' +
                      (fee.feeID || 'custom') +
                      '-' +
                      feeIndex
                    "
                    :class="{ 'is-custom-only': fee.isCustomFeeOnly }"
                  >
                    <td class="text-center">
                      {{ studentIndex + 1 }}.{{ feeIndex + 1 }}
                    </td>
                    <td class="text-center gr-cell">{{ text(student.grNo) }}</td>
                    <td class="text-left font-weight-bold">
                      {{ text(student.name) }}
                    </td>
                    <td class="text-left">{{ text(fee.feeType) }}</td>
                    <td class="text-center">{{ recordPeriod(fee) }}</td>
                    <td class="text-right">{{ formatAmount(fee.netFee) }}</td>
                    <td class="text-right">{{ formatAmount(fee.rcvd) }}</td>
                    <td class="text-right">
                      {{ formatAmount(fee.collectedInPeriod) }}
                    </td>
                    <td class="text-right outstanding-cell">
                      {{ formatAmount(fee.balance) }}
                    </td>
                    <td class="text-center">
                      <span class="status-pill" :class="statusClass(fee.status)">
                        {{ statusText(fee.status) }}
                      </span>
                    </td>
                    <td class="text-center">{{ dateText(fee.rcvdDate) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div v-if="driverBlocks.length" class="grand-total-bar">
        <div class="grand-total-bar__item">
          <span>Billed</span>
          <strong>{{ formatAmount(summary.billed) }}</strong>
        </div>
        <div class="grand-total-bar__item">
          <span>Received</span>
          <strong>{{ formatAmount(summary.received) }}</strong>
        </div>
        <div class="grand-total-bar__item">
          <span>Collected (period)</span>
          <strong>{{ formatAmount(summary.collected) }}</strong>
        </div>
        <div class="grand-total-bar__item">
          <span>Outstanding</span>
          <strong>{{ formatAmount(summary.outstanding) }}</strong>
        </div>
        <div class="grand-total-bar__item">
          <span>Unbilled Custom</span>
          <strong>{{ formatAmount(summary.unbilledCustom) }}</strong>
        </div>
      </div>

      <div class="page-footer">
        <small>
          Powered by MySkool.app | Generated On: {{ currentDateTime }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import { exportObjectsToExcel } from "@/utils/excelExport";
import {
  DRIVER_FEES_EXPORT_COLUMNS,
  buildDriverFeesExportRows,
} from "@/utils/driverFeesReport";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export default {
  name: "DriverFeesReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      notice: "",
      exporting: false,
      driverBlocks: [],
      summary: {
        drivers: 0,
        students: 0,
        feeRecords: 0,
        billed: 0,
        received: 0,
        collected: 0,
        outstanding: 0,
        unbilledCustom: 0,
      },
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      driverID: 0,
      classID: 0,
      sectionID: 0,
      feeTypeID: 0,
      year: 0,
      month: 0,
      status: "all",
      dtFrom: "",
      dtTo: "",
      driverLabel: "",
      classLabel: "",
      sectionLabel: "",
      feeTypeLabel: "",
      monthLabel: "",
      yearLabel: "",
      statusLabel: "",
      receiptLabel: "",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    periodLabel() {
      if (!this.month && !this.year) return "All periods";
      if (this.month && this.year) {
        return `${this.monthLabel || `Month #${this.month}`} ${this.year}`;
      }
      if (this.month) {
        return `${this.monthLabel || `Month #${this.month}`} (all years)`;
      }
      return this.yearLabel || String(this.year);
    },
    effectiveStatusLabel() {
      return (
        this.statusLabel ||
        (this.status === "paid"
          ? "Paid only"
          : this.status === "unpaid"
          ? "Unpaid only"
          : "All statuses")
      );
    },
    effectiveReceiptLabel() {
      if (this.receiptLabel) return this.receiptLabel;
      if (this.dtFrom || this.dtTo) {
        return `${this.dtFrom || "Start"} to ${this.dtTo || "Today"}`;
      }
      return "All receipt dates";
    },
    filterSummary() {
      return [
        this.driverLabel || (this.driverID ? `Driver #${this.driverID}` : "All drivers"),
        this.classLabel || (this.classID ? `Class #${this.classID}` : "All classes"),
        this.sectionLabel ||
          (this.sectionID ? `Section #${this.sectionID}` : "All sections"),
        this.periodLabel,
        this.feeTypeLabel || "Transport fee type",
        this.effectiveStatusLabel,
        this.effectiveReceiptLabel,
      ]
        .filter(Boolean)
        .join(" · ");
    },
    recordCount() {
      return this.driverBlocks.reduce(
        (sum, driver) => sum + (driver.students || []).length,
        0
      );
    },
    feeRecordCount() {
      return this.driverBlocks.reduce(
        (sum, driver) =>
          sum +
          (driver.students || []).reduce(
            (studentSum, student) =>
              studentSum + (student.feeRecords || []).length,
            0
          ),
        0
      );
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID) || this.$store.state.userData.cId || 0;
      this.driverID = Number(query.driverID) || 0;
      this.classID = Number(query.classID) || 0;
      this.sectionID = Number(query.sectionID) || 0;
      this.feeTypeID = Number(query.feeTypeID) || 0;
      this.year = Number(query.year) || 0;
      this.month = Number(query.month) || 0;
      this.status = String(query.status || "all").toLowerCase();
      this.dtFrom = query.dtFrom || "";
      this.dtTo = query.dtTo || "";
      this.driverLabel = query.driverLabel || "";
      this.classLabel = query.classLabel || "";
      this.sectionLabel = query.sectionLabel || "";
      this.feeTypeLabel = query.feeTypeLabel || "";
      this.monthLabel = query.monthLabel || "";
      this.yearLabel = query.yearLabel || "";
      this.statusLabel = query.statusLabel || "";
      this.receiptLabel = query.receiptLabel || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      this.notice = "";

      try {
        const url = `${this.$store.state.domain}Driver/LoadDriverFeesReport`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            driverID: this.driverID,
            classID: this.classID,
            sectionID: this.sectionID,
            feeTypeID: this.feeTypeID,
            year: this.year,
            month: this.month,
            status: this.status || "all",
            dtFrom: this.dtFrom || null,
            dtTo: this.dtTo || null,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload = response.data;
        if (!payload || String(payload.status).toLowerCase() !== "success") {
          this.error =
            payload?.message || "Failed to load the driver fees report.";
          return;
        }

        this.driverBlocks = Array.isArray(payload.data) ? payload.data : [];
        this.notice = payload.message || "";
        this.summary = {
          drivers: toNumber(
            payload.totalDrivers != null
              ? payload.totalDrivers
              : this.driverBlocks.length
          ),
          students: toNumber(
            payload.totalStudents != null
              ? payload.totalStudents
              : this.driverBlocks.reduce(
                  (sum, driver) => sum + (driver.students || []).length,
                  0
                )
          ),
          feeRecords: toNumber(
            payload.totalFeeRecords != null
              ? payload.totalFeeRecords
              : this.feeRecordCount
          ),
          billed: toNumber(payload.totalBilled),
          received: toNumber(payload.totalReceived),
          collected: toNumber(payload.totalCollected),
          outstanding: toNumber(payload.totalOutstanding),
          unbilledCustom: toNumber(payload.totalUnbilledCustom),
        };
      } catch (err) {
        console.error("Driver fees report error:", err);
        this.error = "Failed to retrieve report data.";
      } finally {
        this.loading = false;
      }
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === "") return "0";
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      return n.toLocaleString("en-PK", { maximumFractionDigits: 2 });
    },
    formatCount(value) {
      if (value === null || value === undefined || value === "") return "0";
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      return n.toLocaleString("en-PK");
    },
    text(value) {
      if (value === null || value === undefined) return "—";
      const s = String(value).trim();
      return s && s !== "-" ? s : "—";
    },
    classSection(student) {
      const cls = this.text(student?.className);
      const sec = this.text(student?.sectionName);
      if (cls === "—" && sec === "—") return "—";
      if (cls !== "—" && sec !== "—") return `${cls} / ${sec}`;
      return cls !== "—" ? cls : sec;
    },
    studentVehicle(student) {
      const no = this.text(student?.vehicleNo);
      if (no !== "—") return no;
      return this.text(student?.vehicleBrand);
    },
    vehicleSummary(driver) {
      const vehicles = driver?.vehicles || [];
      return vehicles
        .map((vehicle) => {
          const no = vehicle?.vehicleNo || "";
          const brand = vehicle?.brand ? ` (${vehicle.brand})` : "";
          return `${no}${brand}`.trim();
        })
        .filter(Boolean)
        .join(", ");
    },
    recordPeriod(record) {
      if (record?.monthYear) return record.monthYear;
      const month = Number(record?.month);
      const year = Number(record?.year);
      if (!month && !year) return "—";
      const monthName =
        month >= 1 && month <= 12 ? MONTH_NAMES[month - 1] : "";
      return [monthName, year || ""].filter(Boolean).join(" ") || "—";
    },
    dateText(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    statusText(value) {
      const status = String(value || "").trim();
      if (!status) return "—";
      return status
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    statusClass(value) {
      const status = String(value || "").toLowerCase();
      if (status === "paid") return "status-paid";
      if (status === "not-generated") return "status-missing";
      if (status === "unpaid" || status === "partial" || status === "hold") {
        return "status-unpaid";
      }
      return "";
    },
    async exportExcel() {
      if (this.exporting || !this.driverBlocks.length) return;
      this.exporting = true;
      try {
        const rows = buildDriverFeesExportRows(this.driverBlocks, this.summary);
        if (!rows.length) {
          this.$bvToast.toast("No driver fee records found to export.", {
            title: "Warning",
            variant: "warning",
            solid: true,
          });
          return;
        }

        const stamp = new Date().toISOString().slice(0, 10);
        await exportObjectsToExcel(rows, `Driver_Fees_${stamp}.xlsx`, {
          sheetName: "Driver Fees",
          title: "Driver Fees Report",
          subtitle: this.filterSummary,
          columns: DRIVER_FEES_EXPORT_COLUMNS,
        });
        this.$bvToast.toast("Driver fees report exported successfully.", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Driver fees export error:", error);
        this.$bvToast.toast("Failed to export the driver fees report.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.exporting = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.driver-fees-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }
  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.3);
  }
}

.btn-export {
  background: #1f7a4d;
  color: #fff;
  &:hover {
    background: #19633f;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(31, 122, 77, 0.3);
  }
}

.btn-modern:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1500px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 3px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 100px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }

  .filter-line {
    font-size: 0.95rem;
    color: #636e72;
  }
}

.notice-banner {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #f0c36d;
  border-radius: 8px;
  background: #fff8e6;
  color: #7a5b00;
  font-size: 0.9rem;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;
}

.driver-block {
  margin-bottom: 2rem;
}

.driver-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  page-break-after: avoid;
  page-break-inside: avoid;

  &__left {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    min-width: 0;
    flex: 1;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 6px;
    background: #2c3e50;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  &__name {
    font-weight: 800;
    font-size: 1.05rem;
    color: #2c3e50;
    display: block;
  }

  &__meta {
    font-size: 0.82rem;
    color: #636e72;
    margin-top: 0.25rem;
    line-height: 1.45;
  }

  &__count {
    flex-shrink: 0;
    background: #2c3e50;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.35rem 0.65rem;
    border-radius: 6px;
    white-space: nowrap;
  }
}

.sub-section {
  margin-top: 1rem;
}

.sub-heading {
  margin: 0 0 6px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #2c3e50;
  page-break-after: avoid;
}

.table-container {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 7px 9px;
    box-sizing: border-box;
    font-size: 0.82rem;
    vertical-align: middle;
  }

  thead th {
    background: #2c3e50 !important;
    color: white !important;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.35px;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr.is-custom-only {
    background-color: #fffaf0;
  }

  tfoot .subtotal-row td {
    background: #eef1f5;
    border-top: 2px solid #2c3e50;
    font-size: 0.8rem;
  }

  .gr-cell {
    font-weight: 700;
    color: #2c3e50;
  }

  .outstanding-cell {
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: #b03a2e;
  }
}

.fee-type-table {
  th,
  td {
    font-size: 0.78rem;
    padding: 6px 8px;
  }
}

.fee-records-table {
  th,
  td {
    font-size: 0.74rem;
    padding: 5px 7px;
  }
}

.status-pill {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 999px;
  background: #eef1f5;
  color: #4b5563;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;

  &.status-paid {
    background: #e6f6ec;
    color: #1e7b44;
  }

  &.status-unpaid {
    background: #fdecea;
    color: #b03a2e;
  }

  &.status-missing {
    background: #fdf3e2;
    color: #9c640c;
  }
}

.grand-total-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem 2rem;
  margin-top: 1.25rem;
  padding: 0.9rem 1.25rem;
  background: #2c3e50;
  color: #fff;
  border-radius: 8px;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;

    span {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.75;
    }

    strong {
      font-size: 1rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
    }
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

@media (max-width: 900px) {
  .report-page {
    padding: 1.25rem;
  }
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .driver-fees-report {
    background: white !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    overflow: visible !important;
  }

  .report-header-modern {
    margin-bottom: 10px !important;
    padding-bottom: 8px !important;
    border-bottom: 2px solid #000 !important;
    page-break-inside: avoid;
    break-inside: avoid;

    .school-logo-img {
      height: 60px !important;
      max-width: 160px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.2rem !important;
    }

    .report-info-line {
      font-size: 0.8rem !important;
    }

    .filter-line {
      font-size: 0.72rem !important;
    }
  }

  .notice-banner {
    border: 1px solid #999 !important;
    background: #fff !important;
    color: #000 !important;
    page-break-inside: avoid;
  }

  .driver-block {
    margin-bottom: 10px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .driver-heading {
    padding: 5px 7px !important;
    background: #f1f2f6 !important;
    border: 1px solid #000 !important;
    border-bottom: none !important;
    border-radius: 0 !important;
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &__index,
    &__count {
      background: #333 !important;
      color: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__name {
      font-size: 0.85rem !important;
      color: #000 !important;
    }

    &__meta {
      font-size: 0.65rem !important;
      color: #333 !important;
    }
  }

  .sub-section {
    margin-top: 6px !important;
  }

  .sub-heading {
    font-size: 0.62rem !important;
    margin-bottom: 3px !important;
    color: #000 !important;
  }

  .table-container {
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    overflow: visible !important;
    page-break-inside: auto;
    break-inside: auto;
  }

  .report-table {
    width: 100% !important;
    table-layout: fixed;
    page-break-inside: auto;
    break-inside: auto;

    thead {
      display: table-header-group;
    }

    tbody tr,
    tfoot tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    tfoot {
      display: table-row-group;
    }

    th,
    td {
      padding: 2.5px 4px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
      overflow-wrap: break-word;
    }

    thead th {
      background: #e9e9e9 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr:nth-child(even),
    tbody tr.is-custom-only {
      background: #fff !important;
    }

    tfoot .subtotal-row td {
      background: #f2f2f2 !important;
      border-top: 2px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .outstanding-cell {
      color: #000 !important;
    }
  }

  .fee-type-table {
    font-size: 7.5pt !important;

    th,
    td {
      font-size: 7.5pt !important;
    }
  }

  .students-table {
    font-size: 7pt !important;

    th,
    td {
      font-size: 7pt !important;
      padding: 2px 3px !important;
    }

    thead th {
      font-size: 6.5pt !important;
      letter-spacing: 0 !important;
    }
  }

  .fee-records-table {
    font-size: 6.5pt !important;

    th,
    td {
      font-size: 6.5pt !important;
      padding: 2px 3px !important;
    }

    thead th {
      font-size: 6.2pt !important;
      letter-spacing: 0 !important;
    }
  }

  .status-pill {
    background: transparent !important;
    color: #000 !important;
    padding: 0 !important;
    font-size: 6.8pt !important;
  }

  .grand-total-bar {
    margin-top: 6px !important;
    padding: 5px 8px !important;
    background: #eee !important;
    color: #000 !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    gap: 5px 18px !important;
    page-break-inside: avoid;
    break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &__item strong {
      font-size: 0.75rem !important;
      color: #000 !important;
    }
  }

  .page-footer {
    padding-top: 8px !important;
    margin-top: 10px !important;
    color: #555 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>
